import { useId, useState } from 'react';

import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import useValueToLabelCache from '@/hooks/useValueToLabelCache';
import { cn } from '@/lib/cn';

import IconButton from '../Button/IconButton';
import { PositionCombobox } from '../Combobox/PositionCombobox';
import type { PositionOption } from '../Combobox/PositionCombobox/-query/type';
import Icon from '../Icon/Icon';
import { HStack } from '../Layout';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

export type Position = {
  id: string;
  name: string;
};

type Props = {
  value?: Position;
  onChange: (option?: Position) => void;
  allowEmpty?: boolean;
  disabled?: boolean;
  placeHolder?: string;
};

export function PositionSelect(props: Props) {
  const tc = useCommonTranslation();

  const [open, setOpen] = useState(false);
  const popoverId = useId();

  const valueToLabelCache = useValueToLabelCache({
    base: props.value ? { [props.value.id]: props.value.name } : undefined,
  });

  const handleValueChange = (value: string | null, option?: PositionOption) => {
    if (value && option) {
      props.onChange({ id: option.id, name: option.name });
    } else {
      props.onChange(undefined);
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={() => setOpen(!open)}>
      <HStack>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-controls={popoverId}
            className={cn(
              'justify-between overflow-hidden text-ellipsis',
              !props.value && 'text-muted-foreground',
            )}
            disabled={props.disabled}
          >
            {props.value?.name || props.placeHolder || tc('pleaseSelect')}
            <Icon
              iconName="ChevronDownIcon"
              className="ml-2 shrink-0 opacity-50"
            />
          </Button>
        </PopoverTrigger>
        {props.allowEmpty && !props.disabled && (
          <IconButton
            icon="XIcon"
            variant="ghost"
            size="Small"
            onClick={() => props.onChange(undefined)}
            aria-label="クリア"
          />
        )}
      </HStack>
      <PopoverContent
        id={popoverId}
        className="w-full overflow-hidden p-0"
        align="start"
      >
        <PositionCombobox
          value={props.value?.id ?? null}
          onValueChange={handleValueChange}
          valueToLabelCache={valueToLabelCache}
        />
      </PopoverContent>
    </Popover>
  );
}
